import { h, card, apiClient, btn, pill } from './_common.js';

export async function renderChat(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' }, [
    h('div', { class: 'flex items-center justify-between mb-6' }, [
      h('div', {}, [
        h('h2', { class: 'font-display text-3xl font-bold' }, '💬 IA & Agentes'),
        h('p', { class: 'text-ink-300', id: 'chat-subtitle' }, 'Agentes especializados organizados por categoría + OpenClaw multi-canal. Crea los tuyos.'),
      ]),
      h('a', { href: '/chat/', target: '_blank', class: 'text-gold-400 text-sm hover:text-gold-300' }, 'OpenClaw en pestaña nueva →'),
    ]),
    h('div', { id: 'providers-banner', class: 'mb-3' }),
    h('div', { class: 'flex gap-1 mb-4 border-b border-base-700' }, [
      tabBtn('asistente', '🤖 Asistente', true),
      tabBtn('marketplace', '🛒 Marketplace'),
      tabBtn('mis-agentes', '⭐ Mis agentes'),
      tabBtn('memoria', '🧠 Memoria'),
      tabBtn('crear', '+ Crear agente'),
      tabBtn('openclaw', '🦞 OpenClaw'),
    ]),
    h('div', { id: 'tab-content' }),
    h('div', { id: 'agent-dialog' }),
  ]);
  root.appendChild(wrap);

  // Providers banner
  const provs = await apiClient.get('/api/v1/ai/providers').catch(() => ({ providers: {} }));
  wrap.querySelector('#providers-banner').appendChild(
    h('div', { class: 'bg-base-800/40 border border-base-700 rounded-lg p-3 flex flex-wrap gap-2 items-center text-xs' }, [
      h('span', { class: 'text-ink-300' }, 'Providers disponibles:'),
      provs.providers.claude ? pill('Claude ✓', 'green') : pill('Claude ✗', 'red'),
      provs.providers.openai ? pill('OpenAI ✓', 'green') : pill('OpenAI ✗', 'red'),
      provs.providers.ollama ? pill('Ollama local ✓', 'green') : pill('Ollama ✗', 'red'),
    ]),
  );

  // Tabs
  wrap.querySelectorAll('[data-tab]').forEach((b) => {
    b.addEventListener('click', () => {
      wrap.querySelectorAll('[data-tab]').forEach((x) => x.className = tabClass(false));
      b.className = tabClass(true);
      renderTab(b.dataset.tab, wrap);
    });
  });
  renderTab('asistente', wrap);
}

function tabBtn(id, label, active = false) {
  return h('button', { 'data-tab': id, class: tabClass(active) }, label);
}
function tabClass(active) {
  return active
    ? 'px-4 py-2 text-sm font-medium border-b-2 border-gold-400 text-gold-300'
    : 'px-4 py-2 text-sm font-medium border-b-2 border-transparent text-ink-300 hover:text-ink-100';
}

async function renderTab(id, wrap) {
  const content = wrap.querySelector('#tab-content');
  content.innerHTML = '<p class="text-ink-300 text-sm">Cargando…</p>';

  if (id === 'asistente') return renderAsistente(content);
  if (id === 'marketplace') return renderMarketplace(content, wrap);
  if (id === 'mis-agentes') return renderCustomAgents(content, wrap);
  if (id === 'memoria') return renderMemory(content);
  if (id === 'crear') return renderCreateAgent(content, wrap);
  if (id === 'openclaw') return renderOpenClawEmbed(content);
}

// ============================================================
// 🤖 ASISTENTE — Chatbot único que enruta dinámicamente al mejor de los 900+ agentes
// ============================================================
function renderAsistente(content) {
  content.innerHTML = '';

  const conversation = h('div', { class: 'space-y-3 mb-4 max-h-[60vh] overflow-y-auto' });
  const messages = []; // {role: 'user'|'assistant', text, candidates?, chosen?, expertResponses?, mode?}

  // Selector de modo
  let mode = 'auto';
  const modeBar = h('div', { class: 'flex items-center gap-2 mb-3 text-xs' }, [
    h('span', { class: 'text-ink-300' }, 'Modo:'),
    modeBtn('auto', '⚡ Auto (1 experto)', true),
    modeBtn('panel', '👥 Panel (3 expertos)'),
    modeBtn('choose', '📋 Elegir yo'),
  ]);

  function modeBtn(value, label, active = false) {
    const b = h('button', {
      class: `px-3 py-1 rounded-full border text-xs transition-colors ${active ? 'bg-gold-400/20 text-gold-200 border-gold-400/50' : 'bg-base-800 text-ink-300 border-base-600 hover:border-gold-400/30'}`,
      onclick: () => {
        mode = value;
        modeBar.querySelectorAll('button').forEach((x) => {
          if (x.dataset.mode === value) x.className = 'px-3 py-1 rounded-full border text-xs transition-colors bg-gold-400/20 text-gold-200 border-gold-400/50';
          else x.className = 'px-3 py-1 rounded-full border text-xs transition-colors bg-base-800 text-ink-300 border-base-600 hover:border-gold-400/30';
        });
      },
    }, label);
    b.dataset.mode = value;
    return b;
  }

  // Header explicativo
  const header = h('div', { class: 'bg-base-800/30 border border-base-700 rounded-xl p-4 mb-4' }, [
    h('div', { class: 'flex items-center gap-2 mb-1' }, [
      h('span', { class: 'text-2xl' }, '🤖'),
      h('h3', { class: 'font-display font-semibold text-lg' }, 'Asistente CARLOS OS'),
    ]),
    h('p', { class: 'text-sm text-ink-300' }, [
      'Escribe tu pregunta. El sistema elige automáticamente el mejor agente entre ',
      h('span', { id: 'asistente-agent-count', class: 'text-gold-300 font-semibold' }, '900+'),
      ' especialistas y te responde.',
    ]),
  ]);
  // Fetch count dinámico
  apiClient.get('/api/v1/ai/agents').then((r) => {
    const el = document.getElementById('asistente-agent-count');
    if (el && r && r.total) el.textContent = String(r.total);
  }).catch(() => null);
  content.appendChild(header);
  content.appendChild(modeBar);
  content.appendChild(conversation);

  // Input + send
  const input = h('textarea', {
    placeholder: 'Escribe tu pregunta… p.ej. "¿cómo escalo mi patrimonio inmobiliario en Alicante?"',
    class: 'w-full bg-base-900 border border-base-600 rounded-lg px-4 py-3 text-sm min-h-[80px] focus:border-gold-400 focus:outline-none',
  });
  const sendBtn = h('button', {
    class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-6 py-3 rounded-lg hover:from-gold-300 hover:to-gold-400 transition-all',
    onclick: () => send(),
  }, 'Enviar →');

  // Voz natural — Web Speech API (gratis, browser-native)
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  let recognition = null;
  const micBtn = h('button', {
    class: 'bg-base-700 hover:bg-base-600 border border-base-600 text-ink-100 p-3 rounded-lg transition-all',
    title: 'Hablar (voz)',
    onclick: () => {
      if (!SpeechRecognition) {
        alert('Tu navegador no soporta voz. Usa Chrome o Safari.');
        return;
      }
      if (recognition) {
        recognition.stop();
        recognition = null;
        micBtn.textContent = '🎤';
        micBtn.classList.remove('bg-red-500/30');
        return;
      }
      recognition = new SpeechRecognition();
      recognition.lang = 'es-ES';
      recognition.interimResults = true;
      recognition.continuous = false;
      micBtn.textContent = '⏺';
      micBtn.classList.add('bg-red-500/30');
      recognition.onresult = (e) => {
        let transcript = '';
        for (const r of e.results) transcript += r[0].transcript;
        input.value = transcript;
      };
      recognition.onend = () => {
        recognition = null;
        micBtn.textContent = '🎤';
        micBtn.classList.remove('bg-red-500/30');
      };
      recognition.onerror = (e) => {
        console.warn('Speech error:', e.error);
        recognition = null;
        micBtn.textContent = '🎤';
        micBtn.classList.remove('bg-red-500/30');
      };
      recognition.start();
    },
  }, '🎤');

  // Botón guardar como idea
  const ideaBtn = h('button', {
    class: 'bg-base-700 hover:bg-base-600 border border-base-600 text-ink-100 p-3 rounded-lg transition-all',
    title: 'Guardar como idea (crea nota + tarea Calendario IA)',
    onclick: async () => {
      const text = input.value.trim();
      if (!text) { alert('Escribe primero la idea'); return; }
      ideaBtn.disabled = true;
      ideaBtn.textContent = '⏳';
      try {
        const r = await apiClient.post('/api/v1/ideas', { content: text, source: 'chat', autoSchedule: true });
        const { toast } = await import('./_common.js');
        toast('💡 Idea guardada + añadida a Calendario IA', 'success');
        input.value = '';
      } catch (e) {
        const { toast } = await import('./_common.js');
        toast('Error: ' + (e?.message || ''), 'error');
      } finally {
        ideaBtn.disabled = false;
        ideaBtn.textContent = '💡';
      }
    },
  }, '💡');

  const inputRow = h('div', { class: 'flex gap-2 items-end' }, [input, micBtn, ideaBtn, sendBtn]);
  content.appendChild(inputRow);

  // Text-to-speech para respuestas del asistente
  function speakResponse(text) {
    if (!('speechSynthesis' in window)) return;
    try {
      const utterance = new SpeechSynthesisUtterance(text.slice(0, 500));
      utterance.lang = 'es-ES';
      utterance.rate = 1.05;
      utterance.pitch = 1.0;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    } catch { /* skip */ }
  }
  // Expongo function global para que assistantResponse la use al hacer click en 🔊
  window.__carlosSpeak = speakResponse;

  // Submit con Enter (sin shift)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  });

  async function send() {
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    sendBtn.disabled = true;
    sendBtn.textContent = 'Pensando…';

    // Bubble del user
    messages.push({ role: 'user', text });
    conversation.appendChild(messageBubble('user', text));
    conversation.scrollTop = conversation.scrollHeight;

    // Placeholder de "thinking"
    const thinking = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-3 text-sm text-ink-300 italic' }, '⏳ Clasificando + ejecutando agente…');
    conversation.appendChild(thinking);
    conversation.scrollTop = conversation.scrollHeight;

    try {
      const r = await apiClient.post('/api/v1/ai/dispatch', { input: text, mode });
      thinking.remove();
      conversation.appendChild(assistantResponse(r, text));
      conversation.scrollTop = conversation.scrollHeight;
    } catch (e) {
      thinking.remove();
      conversation.appendChild(messageBubble('error', '❌ Error: ' + (e.message || 'unknown')));
    } finally {
      sendBtn.disabled = false;
      sendBtn.textContent = 'Enviar →';
    }
  }

  function messageBubble(role, text) {
    const isUser = role === 'user';
    return h('div', { class: `flex ${isUser ? 'justify-end' : 'justify-start'}` }, [
      h('div', { class: `max-w-3xl rounded-xl px-4 py-3 text-sm whitespace-pre-line ${
        isUser ? 'bg-gold-400/15 border border-gold-400/30 text-ink-100' :
        role === 'error' ? 'bg-red-400/15 border border-red-400/30 text-red-200' :
        'bg-base-800/60 border border-base-700 text-ink-100'
      }` }, text),
    ]);
  }

  function assistantResponse(r, originalInput) {
    const wrap = h('div', { class: 'space-y-2' });

    // Mode "choose" — muestra candidatos para elegir
    if (r.mode === 'choose' || (r.candidates && !r.output && !r.synthesis)) {
      wrap.appendChild(h('div', { class: 'text-xs text-ink-300 mb-1' }, '3 agentes que mejor encajan:'));
      r.candidates.forEach((c) => {
        wrap.appendChild(h('button', {
          class: 'w-full text-left bg-base-800/60 border border-base-700 rounded-xl p-3 hover:border-gold-400/40 transition-colors',
          onclick: async () => {
            // Ejecuta ese agente individual
            sendBtn.disabled = true;
            sendBtn.textContent = 'Ejecutando…';
            try {
              const r2 = await apiClient.post(`/api/v1/ai/agents/${c.id}/run`, { input: originalInput });
              conversation.appendChild(h('div', { class: 'bg-base-800/60 border border-base-700 rounded-xl p-3' }, [
                h('div', { class: 'flex items-center gap-2 mb-2' }, [
                  h('span', { class: 'text-xl' }, c.emoji),
                  h('span', { class: 'font-semibold' }, c.name),
                ]),
                h('div', { class: 'text-sm whitespace-pre-line' }, r2.content || r2.output || JSON.stringify(r2)),
              ]));
              conversation.scrollTop = conversation.scrollHeight;
            } catch (e) {
              const { toast } = await import('./_common.js'); toast('Error: ' + e.message, 'error');
            } finally {
              sendBtn.disabled = false;
              sendBtn.textContent = 'Enviar →';
            }
          },
        }, [
          h('div', { class: 'flex items-center gap-2' }, [
            h('span', { class: 'text-2xl' }, c.emoji),
            h('div', { class: 'flex-1' }, [
              h('div', { class: 'font-semibold' }, c.name),
              h('div', { class: 'text-xs text-gold-300 mt-0.5' }, c.reason),
            ]),
          ]),
        ]));
      });
      return wrap;
    }

    // Mode "auto" — muestra solo la respuesta del top 1
    if (r.mode === 'auto' && r.chosen) {
      wrap.appendChild(h('div', { class: 'flex items-center gap-2 text-xs text-ink-400 mb-1' }, [
        h('span', { class: 'text-base' }, r.chosen.emoji),
        h('span', {}, `${r.chosen.name} ·`),
        h('span', { class: 'italic' }, r.chosen.reason),
      ]));
      wrap.appendChild(messageBubble('assistant', r.output));
      return wrap;
    }

    // Mode "panel" — muestra síntesis + acordeón con expertos
    if (r.mode === 'panel' && r.synthesis) {
      wrap.appendChild(h('div', { class: 'text-xs text-ink-300 mb-1' }, `📋 Síntesis de ${r.expertResponses?.length || 3} expertos:`));
      wrap.appendChild(messageBubble('assistant', r.synthesis));
      if (r.expertResponses?.length) {
        const details = h('details', { class: 'mt-2' }, [
          h('summary', { class: 'text-xs text-gold-400 cursor-pointer hover:underline' }, 'Ver respuestas individuales →'),
          h('div', { class: 'mt-2 space-y-2' },
            r.expertResponses.map((ex) => h('div', { class: 'bg-base-900 border border-base-700 rounded-lg p-3' }, [
              h('div', { class: 'flex items-center gap-2 mb-1' }, [
                h('span', { class: 'text-base' }, ex.emoji),
                h('span', { class: 'font-medium text-sm' }, ex.name),
              ]),
              h('div', { class: 'text-xs whitespace-pre-line text-ink-200' }, ex.output),
            ])),
          ),
        ]);
        wrap.appendChild(details);
      }
      return wrap;
    }

    // Fallback
    wrap.appendChild(messageBubble('assistant', JSON.stringify(r, null, 2)));
    return wrap;
  }
}

async function renderMarketplace(content, wrap) {
  const r = await apiClient.get('/api/v1/ai/agents').catch(() => ({ agents: [], categories: [], total: 0 }));
  content.innerHTML = '';

  // Sincroniza el subtítulo del chat con el conteo real
  const subtitle = document.getElementById('chat-subtitle');
  if (subtitle && r.total) subtitle.textContent = `${r.total} agentes especializados en ${r.categories?.length || 0} categorías + OpenClaw multi-canal. Crea los tuyos.`;

  // Header con buscador + filtro categoría
  const search = h('input', {
    type: 'search',
    placeholder: `🔍 Buscar entre ${r.total ?? r.agents.length} agentes…`,
    class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm',
  });
  const catFilter = h('select', { class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' }, [
    h('option', { value: '' }, 'Todas las categorías'),
    ...(r.categories || []).map((c) => h('option', { value: c.id }, `${c.emoji} ${c.label} (${c.count})`)),
  ]);
  content.appendChild(h('div', { class: 'flex gap-2 mb-4 flex-wrap' }, [search, catFilter]));

  // Stats fila
  if (r.categories?.length) {
    content.appendChild(h('div', { class: 'flex flex-wrap gap-1 mb-4 text-xs' },
      r.categories.map((c) => h('button', {
        class: 'px-2 py-1 rounded-full border border-base-700 bg-base-800/50 hover:border-gold-400/40 transition-colors',
        onclick: () => { catFilter.value = c.id; render(); },
      }, `${c.emoji} ${c.label} ${c.count}`)),
    ));
  }

  const container = h('div');
  content.appendChild(container);

  function render() {
    const q = search.value.trim().toLowerCase();
    const cat = catFilter.value;
    const filtered = r.agents.filter((a) => {
      if (cat && a.category !== cat) return false;
      if (q && !(a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q) || a.id.includes(q))) return false;
      return true;
    });
    container.innerHTML = '';
    if (filtered.length === 0) {
      container.appendChild(h('div', { class: 'text-center py-12 text-ink-300' }, [
        h('div', { class: 'text-5xl mb-3' }, '🔍'),
        h('p', {}, 'Ningún agente coincide con la búsqueda.'),
      ]));
      return;
    }
    // Agrupar por categoría preservando orden
    const groups = new Map();
    const catOrder = (r.categories || []).map((c) => c.id);
    filtered.forEach((a) => {
      const k = a.category || 'personal';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(a);
    });
    const sortedKeys = [...groups.keys()].sort((a, b) => catOrder.indexOf(a) - catOrder.indexOf(b));
    sortedKeys.forEach((k) => {
      const meta = (r.categories || []).find((c) => c.id === k) || { label: k, emoji: '📁' };
      container.appendChild(h('div', { class: 'mb-6' }, [
        h('div', { class: 'flex items-center gap-2 mb-2' }, [
          h('h3', { class: 'font-display font-semibold text-base' }, `${meta.emoji} ${meta.label}`),
          h('span', { class: 'text-xs text-ink-400' }, `${groups.get(k).length}`),
        ]),
        h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' },
          groups.get(k).map((a) => agentCard(a, wrap.querySelector('#agent-dialog'), 'builtin')),
        ),
      ]));
    });
  }

  search.addEventListener('input', render);
  catFilter.addEventListener('change', render);
  render();
}

async function renderCustomAgents(content, wrap) {
  const r = await apiClient.get('/api/v1/ai/custom-agents').catch(() => ({ agents: [] }));
  content.innerHTML = '';
  if (!r.agents.length) {
    content.appendChild(h('div', { class: 'text-center py-12' }, [
      h('div', { class: 'text-5xl mb-3' }, '🤖'),
      h('h3', { class: 'font-display font-semibold' }, 'No has creado agentes propios'),
      h('p', { class: 'text-sm text-ink-300 mt-1' }, 'Pulsa "+ Crear agente" para empezar.'),
    ]));
    return;
  }
  content.appendChild(h('p', { class: 'text-sm text-ink-300 mb-3' }, `${r.agents.length} agente${r.agents.length === 1 ? '' : 's'} personalizado${r.agents.length === 1 ? '' : 's'}. Toca uno para usarlo. Pulsa ✎ para editar o ✕ para borrar.`));
  const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
  r.agents.forEach((a) => grid.appendChild(customAgentCard(a, wrap.querySelector('#agent-dialog'), () => renderCustomAgents(content, wrap))));
  content.appendChild(grid);
}

function customAgentCard(a, dialogRoot, refresh) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 hover:border-gold-400/40 transition-colors' }, [
    h('div', { class: 'flex items-start justify-between mb-2' }, [
      h('div', { class: 'flex items-center gap-2 cursor-pointer flex-1', onclick: () => openAgentDialog(a, dialogRoot, 'custom') }, [
        h('span', { class: 'text-3xl' }, a.emoji || '🤖'),
        h('div', {}, [
          h('div', { class: 'font-semibold' }, a.name),
          h('p', { class: 'text-xs text-ink-300 line-clamp-2' }, a.description || ''),
        ]),
      ]),
      h('div', { class: 'flex gap-1' }, [
        h('button', { class: 'text-xs text-gold-400 hover:text-gold-300 px-1', title: 'Editar', onclick: () => openCustomAgentEditor(a, refresh) }, '✎'),
        h('button', { class: 'text-xs text-red-400 hover:text-red-300 px-1', title: 'Borrar', onclick: async () => {
          if (!confirm(`¿Borrar agente "${a.name}"?`)) return;
          await apiClient.delete(`/api/v1/ai/custom-agents/${a.id}`);
          refresh();
        } }, '✕'),
      ]),
    ]),
  ]);
}

function openCustomAgentEditor(a, refresh) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-base-900/80 backdrop-blur z-50 flex items-center justify-center p-6',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  });
  const f = {
    emoji: h('input', { type: 'text', value: a.emoji || '🤖', maxlength: 4, class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 w-20 text-center' }),
    name: h('input', { type: 'text', value: a.name || '', class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-3 py-2' }),
    description: h('input', { type: 'text', value: a.description || '', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2' }),
    systemPrompt: h('textarea', { rows: 12, class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm font-mono' }),
    needsContext: h('input', { type: 'checkbox' }),
  };
  f.systemPrompt.value = a.systemPrompt || '';
  if (a.needsContext) f.needsContext.checked = true;
  const msg = h('div', { class: 'text-sm' });

  const dialog = h('div', { class: 'bg-base-800 border border-gold-400/40 rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-auto' }, [
    h('div', { class: 'flex items-center justify-between mb-4' }, [
      h('h3', { class: 'font-display font-bold text-lg' }, `✎ Editar agente`),
      h('button', { class: 'text-ink-300 hover:text-ink-100', onclick: () => overlay.remove() }, '✕'),
    ]),
    h('div', { class: 'flex gap-2 mb-3' }, [f.emoji, f.name]),
    h('label', { class: 'block text-xs text-ink-300 mb-1' }, 'Descripción corta'),
    f.description,
    h('label', { class: 'block text-xs text-ink-300 mb-1 mt-3' }, 'System prompt'),
    f.systemPrompt,
    h('label', { class: 'flex items-center gap-2 text-sm mt-2' }, [f.needsContext, h('span', {}, 'Incluir contexto del usuario (datos de cuenta)')]),
    msg,
    h('div', { class: 'flex gap-2 mt-4' }, [
      btn('Guardar', async () => {
        try {
          await apiClient.patch(`/api/v1/ai/custom-agents/${a.id}`, {
            emoji: f.emoji.value, name: f.name.value, description: f.description.value,
            systemPrompt: f.systemPrompt.value, needsContext: f.needsContext.checked,
          });
          overlay.remove();
          refresh();
        } catch (e) { msg.textContent = e.message; msg.className = 'text-sm text-red-300'; }
      }),
      btn('Cancelar', () => overlay.remove(), { variant: 'secondary' }),
    ]),
  ]);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);
}

async function renderMemory(content) {
  const r = await apiClient.get('/api/v1/ai/memory').catch(() => ({ memories: [] }));
  content.innerHTML = '';

  // Formulario añadir
  const form = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 mb-4 max-w-2xl' }, [
    h('h3', { class: 'font-semibold mb-2' }, '+ Añadir memoria'),
    h('p', { class: 'text-xs text-ink-300 mb-3' }, 'Hechos sobre ti, gente, preferencias, contexto. La IA los usará automáticamente.'),
  ]);
  const typeSel = h('select', { class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm mb-2' }, [
    h('option', { value: 'fact' }, 'Hecho'),
    h('option', { value: 'preference' }, 'Preferencia'),
    h('option', { value: 'relationship' }, 'Relación / persona'),
    h('option', { value: 'context' }, 'Contexto'),
  ]);
  const importanceInput = h('input', { type: 'number', min: 1, max: 10, value: 7, class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-24 mb-2 ml-2', title: 'Importancia 1-10' });
  const contentTxt = h('textarea', { rows: 2, placeholder: 'Ej: Mi cumpleaños es el 5 de mayo · Prefiero respuestas directas · Trabajo mejor por las mañanas', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm mb-2' });
  const msg = h('div', { class: 'text-sm mb-2' });
  form.appendChild(h('div', { class: 'flex items-center gap-2 mb-2' }, [typeSel, h('label', { class: 'text-xs text-ink-300' }, 'Importancia'), importanceInput]));
  form.appendChild(contentTxt);
  form.appendChild(msg);
  form.appendChild(btn('Guardar memoria', async () => {
    if (!contentTxt.value.trim()) return;
    try {
      await apiClient.post('/api/v1/ai/memory', { type: typeSel.value, content: contentTxt.value, importance: Number(importanceInput.value) });
      contentTxt.value = '';
      renderMemory(content);
    } catch (e) {
      msg.textContent = 'Error: ' + e.message;
      msg.className = 'text-sm text-red-300';
    }
  }));
  content.appendChild(form);

  if (!r.memories.length) {
    content.appendChild(h('p', { class: 'text-sm text-ink-300 text-center mt-4' }, 'Sin memorias aún.'));
    return;
  }
  const list = h('div', { class: 'space-y-2' });
  r.memories.forEach((m) => list.appendChild(memoryRow(m, () => renderMemory(content))));
  content.appendChild(list);
}

function memoryRow(m, refresh) {
  return h('div', { class: 'bg-base-800/40 border border-base-700 rounded-lg p-3 flex items-start gap-3' }, [
    pill(m.type, 'gold'),
    h('div', { class: 'flex-1 text-sm' }, m.content),
    h('span', { class: 'text-xs text-ink-400' }, `${m.importance ?? 5}/10`),
    h('button', { class: 'text-red-400 hover:text-red-300 text-xs', onclick: async () => {
      if (!confirm('¿Borrar?')) return;
      await apiClient.delete(`/api/v1/ai/memory/${m.id}`);
      refresh();
    } }, '✕'),
  ]);
}

function renderCreateAgent(content, wrap) {
  content.innerHTML = '';
  const form = h('div', { class: 'max-w-2xl space-y-3' });
  const name = h('input', { type: 'text', placeholder: 'Nombre del agente', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2' });
  const emoji = h('input', { type: 'text', placeholder: '🤖', maxlength: 4, class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 w-20 text-center' });
  const desc = h('input', { type: 'text', placeholder: 'Descripción corta (1 línea)', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2' });
  const prompt = h('textarea', { rows: 8, placeholder: 'System prompt: cómo debe comportarse el agente. Sé específico.\n\nEjemplo:\nEres asistente experto en X. Hablas en español. Cuando Carlos te pregunte:\n1. ...\n2. ...', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm font-mono' });
  const needsCtx = h('label', { class: 'flex items-center gap-2 text-sm' }, [
    h('input', { type: 'checkbox' }), h('span', {}, 'Incluir contexto del usuario (datos de cuenta)'),
  ]);
  const tempInput = h('input', { type: 'range', min: 0, max: 10, value: 7, class: 'flex-1' });
  const tempLabel = h('span', { class: 'text-xs text-ink-300 w-10 text-right' }, '0.7');
  tempInput.addEventListener('input', () => tempLabel.textContent = (tempInput.value / 10).toFixed(1));

  const msg = h('div', { class: 'text-sm' });
  form.appendChild(h('div', { class: 'flex gap-2' }, [emoji, name]));
  form.appendChild(desc);
  form.appendChild(h('label', { class: 'text-xs text-ink-300' }, 'System prompt'));
  form.appendChild(prompt);
  form.appendChild(needsCtx);
  form.appendChild(h('div', { class: 'flex items-center gap-2' }, [
    h('span', { class: 'text-xs text-ink-300' }, 'Temperatura (creatividad):'),
    tempInput, tempLabel,
  ]));
  form.appendChild(msg);
  form.appendChild(btn('Crear agente', async () => {
    if (!name.value || prompt.value.length < 10) {
      msg.textContent = 'Nombre + prompt mín 10 chars requeridos';
      msg.className = 'text-sm text-red-300';
      return;
    }
    try {
      await apiClient.post('/api/v1/ai/custom-agents', {
        name: name.value,
        emoji: emoji.value || '🤖',
        description: desc.value || '',
        systemPrompt: prompt.value,
        needsContext: needsCtx.querySelector('input').checked,
        temperature: Number(tempInput.value),
      });
      msg.textContent = '✓ Agente creado. Mira la pestaña "Mis agentes".';
      msg.className = 'text-sm text-emerald-300';
      name.value = ''; emoji.value = ''; desc.value = ''; prompt.value = '';
    } catch (e) {
      msg.textContent = 'Error: ' + e.message;
      msg.className = 'text-sm text-red-300';
    }
  }));
  content.appendChild(form);
}

function renderOpenClawEmbed(content) {
  content.innerHTML = '';
  content.appendChild(h('div', { class: 'bg-base-800 border border-base-700 rounded-xl overflow-hidden', style: 'height: 70vh' }, [
    h('iframe', { src: '/chat/', class: 'w-full h-full border-0', title: 'OpenClaw', allow: 'clipboard-read; clipboard-write; microphone; camera' }),
  ]));
}

function agentCard(agent, dialogMount, kind) {
  return h('button', {
    type: 'button',
    class: 'text-left bg-base-800/50 border border-base-700 rounded-xl p-4 hover:border-gold-400/40 transition-colors',
    onclick: () => openAgentDialog(agent, dialogMount, kind),
  }, [
    h('div', { class: 'flex items-start gap-3' }, [
      h('div', { class: 'text-3xl' }, agent.emoji),
      h('div', { class: 'flex-1' }, [
        h('div', { class: 'font-semibold mb-0.5' }, agent.name),
        h('p', { class: 'text-xs text-ink-300 line-clamp-2' }, agent.description),
        agent.needsContext ? h('p', { class: 'text-[10px] text-gold-400 mt-1' }, '+ contexto de tu cuenta') : null,
      ]),
    ]),
  ]);
}

function openAgentDialog(agent, mount, kind) {
  mount.innerHTML = '';
  const overlay = h('div', {
    class: 'fixed inset-0 bg-base-900/80 backdrop-blur z-50 flex items-center justify-center p-6',
    onclick: (e) => { if (e.target === overlay) mount.innerHTML = ''; },
  });
  const dialog = h('div', { class: 'bg-base-800 border border-gold-400/40 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-soft' }, [
    h('div', { class: 'flex items-center justify-between p-5 border-b border-base-700' }, [
      h('div', { class: 'flex items-center gap-3' }, [
        h('div', { class: 'text-3xl' }, agent.emoji),
        h('div', {}, [
          h('h3', { class: 'font-display font-bold text-lg' }, agent.name),
          h('p', { class: 'text-xs text-ink-300' }, agent.description),
        ]),
      ]),
      h('button', { class: 'text-ink-300 hover:text-ink-100', onclick: () => mount.innerHTML = '' }, '✕'),
    ]),
    h('div', { id: 'agent-conversation', class: 'flex-1 overflow-y-auto p-5 space-y-3' }, [
      h('p', { class: 'text-sm text-ink-300 italic' }, 'Escribe tu pregunta y pulsa Enviar.'),
    ]),
    h('div', { class: 'p-4 border-t border-base-700 flex gap-2' }, [
      h('textarea', {
        id: 'agent-input',
        class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm text-ink-100 focus:border-gold-400 focus:outline-none resize-none',
        rows: 2,
        placeholder: '¿En qué puedo ayudarte?',
        onkeydown: (e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            sendAgentMessage(agent, kind);
          }
        },
      }),
      h('button', {
        class: 'bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-300 hover:to-gold-400 text-base-900 font-semibold px-4 py-2 rounded-lg text-sm',
        onclick: () => sendAgentMessage(agent, kind),
      }, 'Enviar'),
    ]),
  ]);
  overlay.appendChild(dialog);
  mount.appendChild(overlay);
  setTimeout(() => document.getElementById('agent-input')?.focus(), 100);
}

async function sendAgentMessage(agent, kind) {
  const input = document.getElementById('agent-input');
  const conv = document.getElementById('agent-conversation');
  if (!input || !conv) return;
  const text = input.value.trim();
  if (!text) return;
  input.value = '';
  input.disabled = true;

  conv.appendChild(h('div', { class: 'flex justify-end' }, [
    h('div', { class: 'bg-gold-400/15 border border-gold-400/30 rounded-2xl rounded-tr-sm px-4 py-2 max-w-[85%] text-sm whitespace-pre-wrap' }, text),
  ]));
  const botMsg = h('div', { class: 'flex justify-start' }, [
    h('div', { class: 'bg-base-700 border border-base-600 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[85%] text-sm text-ink-300' }, 'Pensando…'),
  ]);
  conv.appendChild(botMsg);
  conv.scrollTop = conv.scrollHeight;

  const url = kind === 'custom'
    ? `/api/v1/ai/custom-agents/${agent.id}/run`
    : `/api/v1/ai/agents/${agent.id}/run`;

  try {
    const res = await apiClient.post(url, { input: text });
    botMsg.innerHTML = '';
    botMsg.appendChild(h('div', { class: 'bg-base-700 border border-base-600 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] text-sm whitespace-pre-wrap' }, [
      res.content || '(respuesta vacía)',
      h('div', { class: 'mt-2 text-[10px] text-ink-400 flex gap-2' }, [
        h('span', {}, res.provider),
        h('span', {}, res.model),
        res.usage ? h('span', {}, `↑${res.usage.in ?? '?'} ↓${res.usage.out ?? '?'}`) : null,
      ]),
    ]));
  } catch (e) {
    botMsg.innerHTML = '';
    botMsg.appendChild(h('div', { class: 'bg-red-400/15 border border-red-400/40 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[85%] text-sm text-red-300' }, 'Error: ' + (e.message || 'desconocido')));
  } finally {
    input.disabled = false;
    input.focus();
    conv.scrollTop = conv.scrollHeight;
  }
}
